import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Briefcase, Clock, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Job {
  id: string | number;
  title: string;
  company: string;
  location: string;
  type: string;
  salary: string;
  posted?: string;
  description: string;
  openToRecords?: boolean;
  source?: string;
  applicationUrl?: string;
}

export default function JobSearch() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);

  // Sample local jobs
  const localJobs: Job[] = [];

  const fetchReedJobs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-reed-jobs', {
        body: {
          keywords: searchTerm || 'warehouse',
          location: location || 'UK',
          maxResults: 10
        }
      });

      if (error) throw error;

      if (data?.jobs) {
        setJobs([...localJobs, ...data.jobs]);
      } else {
        setJobs(localJobs);
      }
    } catch (error) {
      console.error('Error fetching Reed jobs:', error);
      toast.error('Could not load external jobs, showing local listings');
      setJobs(localJobs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReedJobs();
  }, []);

  const handleSearch = () => {
    fetchReedJobs();
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = job.location.toLowerCase().includes(location.toLowerCase());
    return matchesSearch && matchesLocation;
  });

  const handleApply = (job: Job) => {
    if (job.applicationUrl) {
      window.open(job.applicationUrl, '_blank');
    } else {
      navigate("/job-application", { state: { job } });
    }
  };

  const openIndeed = () => {
    const query = searchTerm || 'warehouse';
    const loc = location || 'UK';
    window.open(`https://uk.indeed.com/jobs?q=${encodeURIComponent(query)}&l=${encodeURIComponent(loc)}`, '_blank');
  };

  const openCVLibrary = () => {
    const query = searchTerm || 'warehouse';
    const loc = location || 'UK';
    window.open(`https://www.cv-library.co.uk/search-jobs?q=${encodeURIComponent(query)}&geo=${encodeURIComponent(loc)}`, '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Find Your Next Opportunity
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Browse jobs from employers who believe in second chances
            </p>
          </div>

          {/* Search Section */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Job title or company"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
                <div className="flex-1 relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  {loading ? 'Searching...' : 'Search Jobs'}
                </Button>
              </div>

              {/* Affiliate Link Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <p className="text-sm text-muted-foreground flex-shrink-0 self-center">
                  Search more jobs on:
                </p>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openIndeed}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Indeed
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openCVLibrary}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    CV Library
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredJobs.length}</span> opportunities
            </p>
          </div>

          {/* Job Listings */}
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card key={job.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-xl">{job.title}</CardTitle>
                        {job.source && (
                          <Badge variant="outline" className="text-xs">
                            {job.source}
                          </Badge>
                        )}
                      </div>
                      <p className="text-lg font-semibold text-primary">{job.company}</p>
                    </div>
                    <Button onClick={() => handleApply(job)}>
                      {job.applicationUrl ? 'View Job' : 'Apply Now'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">{job.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        {job.type}
                      </div>
                      {job.posted && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {job.posted}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                      <Badge variant="secondary">{job.salary}</Badge>
                      {job.openToRecords && (
                        <Badge className="bg-accent/10 text-accent hover:bg-accent/20">
                          Open to Criminal Records
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredJobs.length === 0 && !loading && (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">
                No jobs found matching your criteria. Try adjusting your search.
              </p>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
